import type { Board, WorkflowPhase } from './api';

/**
 * One row in the Command Center's unified lane view.
 * One entry per unique phase key (column.status) across every board.
 */
export interface PhaseUnionEntry {
  key: string;
  displayName: string;
  color?: string;
  presentInBoards: string[];
  isAutoAdvance: boolean;
}

export interface BuildPhaseUnionInput {
  boards: Pick<Board, 'id' | 'columns'>[];
  /** Optional workflow lookup per boardId — supplies color + autoAdvance metadata. */
  workflowsByBoardId?: Record<string, WorkflowPhase[] | undefined>;
  /** Canonical phase-key order; used to break topological ties so the layout matches
   *  the default template even when individual boards omit phases. */
  canonicalOrder?: string[];
}

/** Default-template phase order — used for stable tie-breaking. */
export const DEFAULT_CANONICAL_ORDER: string[] = [
  'backlog',
  'triage',
  'planning',
  'plan-review',
  'implementation',
  'testing',
  'review',
  'deployment',
  'monitoring',
  'done',
];

/**
 * Merge every board's columns into a single phase-ordered list.
 *
 * Rules:
 * 1. Phases sharing a key (column.status) across boards are deduped into one entry.
 * 2. Order is a topological sort of per-board adjacency pairs.
 *    Ties between independent phases are broken by canonical order, then alphabetical.
 *    Cycles (boards disagreeing on order) fall back to canonical order.
 * 3. Display name = first column.name we see for that key (stable by sorted board id).
 * 4. Color + autoAdvance sourced from the first matching workflow phase, same stable order.
 */
export function buildPhaseUnion({
  boards,
  workflowsByBoardId = {},
  canonicalOrder = DEFAULT_CANONICAL_ORDER,
}: BuildPhaseUnionInput): PhaseUnionEntry[] {
  const sortedBoards = [...boards].sort((a, b) => a.id.localeCompare(b.id));

  // Gather unique phase keys + per-key metadata, stable over board sort.
  const entries = new Map<string, PhaseUnionEntry>();

  for (const board of sortedBoards) {
    const sortedCols = [...board.columns].sort((a, b) => a.order - b.order);
    const wf = workflowsByBoardId[board.id];

    for (const col of sortedCols) {
      const key = col.status;
      const phase = wf?.find((p) => p.name === key);
      const existing = entries.get(key);

      if (!existing) {
        entries.set(key, {
          key,
          displayName: col.name || titleCase(key),
          color: phase?.color,
          presentInBoards: [board.id],
          isAutoAdvance: !!phase?.autoAdvance,
        });
      } else {
        if (!existing.color && phase?.color) existing.color = phase.color;
        if (!existing.presentInBoards.includes(board.id)) {
          existing.presentInBoards.push(board.id);
        }
        if (phase?.autoAdvance) existing.isAutoAdvance = true;
      }
    }
  }

  // Build adjacency graph from each board's column order.
  const successors = new Map<string, Set<string>>();
  const inDegree = new Map<string, number>();
  for (const key of entries.keys()) {
    successors.set(key, new Set());
    inDegree.set(key, 0);
  }

  for (const board of sortedBoards) {
    const sortedCols = [...board.columns].sort((a, b) => a.order - b.order);
    for (let i = 0; i < sortedCols.length - 1; i++) {
      const prev = sortedCols[i].status;
      const next = sortedCols[i + 1].status;
      if (prev === next) continue;
      const set = successors.get(prev);
      if (set && !set.has(next)) {
        set.add(next);
        inDegree.set(next, (inDegree.get(next) ?? 0) + 1);
      }
    }
  }

  // Kahn's with priority tie-breaking (canonical index, then alphabetical).
  const canonicalIndex = new Map<string, number>();
  canonicalOrder.forEach((k, i) => canonicalIndex.set(k, i));
  const rank = (k: string) => canonicalIndex.get(k) ?? Number.POSITIVE_INFINITY;
  const compare = (a: string, b: string) => {
    const ra = rank(a);
    const rb = rank(b);
    if (ra !== rb) return ra - rb;
    return a.localeCompare(b);
  };

  const ready: string[] = [];
  for (const [k, d] of inDegree) if (d === 0) ready.push(k);
  ready.sort(compare);

  const emitted = new Set<string>();
  const ordered: string[] = [];
  while (ready.length > 0) {
    ready.sort(compare);
    const k = ready.shift()!;
    if (emitted.has(k)) continue;
    emitted.add(k);
    ordered.push(k);
    for (const next of successors.get(k) ?? []) {
      const d = (inDegree.get(next) ?? 0) - 1;
      inDegree.set(next, d);
      if (d === 0) ready.push(next);
    }
  }

  // Cycle fallback — any remaining keys are appended in canonical/alphabetical order.
  if (ordered.length < entries.size) {
    const remaining = Array.from(entries.keys()).filter((k) => !emitted.has(k));
    remaining.sort(compare);
    for (const k of remaining) ordered.push(k);
  }

  return ordered.map((k) => entries.get(k)!);
}

function titleCase(slug: string): string {
  return slug
    .split(/[-_]/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}
